/**
 * Ask a doctor: the patient's questions (patient session), doctors' answers (staff session) and ClinVea's
 * review queue (super admin). Answered questions are read by the public site, not the app.
 */
import { fetchApi, fetchPatientApi } from '../client';
import type { PagedResponse } from '../../../types/pagination';

export type QuestionStatus = 'PENDING' | 'OPEN' | 'REJECTED' | 'WITHDRAWN' | 'ERASED';
export type AnswerStatus = 'PENDING' | 'PUBLISHED' | 'REJECTED' | 'WITHDRAWN';

export interface PublishedAnswer {
  doctorName: string;
  doctorSlug: string;
  specialization?: string | null;
  body: string;
  answeredAt?: string | null;
}

export interface MyQuestion {
  id: string;
  title: string;
  details: string;
  specialty?: string | null;
  status: QuestionStatus;
  reviewNote?: string | null;
  /** Set while the question is on ClinVea Care (/questions/{slug}). */
  slug?: string | null;
  askedAt: string;
  answers: PublishedAnswer[];
}

export interface AskInput {
  title: string;
  details: string;
  specialty?: string | null;
  consent: boolean;
}

export interface MyAnswer {
  id: string;
  questionId: string;
  questionTitle?: string | null;
  body: string;
  status: AnswerStatus;
  reviewNote?: string | null;
  updatedAt: string;
}

/** What a doctor sees of an open question — nothing about the patient. */
export interface OpenQuestion {
  id: string;
  title: string;
  details: string;
  specialty?: string | null;
  askedAt: string;
  answerCount: number;
  myAnswer?: MyAnswer | null;
}

export interface QuestionForReview {
  id: string;
  title: string;
  details: string;
  specialty?: string | null;
  flags: string[];
  askedAt: string;
}

export interface AnswerForReview {
  id: string;
  questionTitle: string;
  questionDetails: string;
  body: string;
  doctorName?: string | null;
  doctorSlug?: string | null;
  flags: string[];
  republish: boolean;
  submittedAt: string;
}

export interface QaReviewQueue {
  questions: QuestionForReview[];
  answers: AnswerForReview[];
}

/** A live answer on ClinVea Care, as the super admin sees it (to retract it or close its question). */
export interface LiveAnswer {
  id: string;
  questionId: string;
  questionTitle: string;
  questionSlug?: string | null;
  body: string;
  doctorName?: string | null;
  publishedAt: string;
}

/** React Query key for the waiting counts — shared by the super-admin menu badges and the review pages. */
export const MODERATION_COUNTS_KEY = ['admin', 'moderationCounts'] as const;

/** Items waiting in each super-admin review queue. */
export interface ModerationPendingCounts {
  questions: number;
  answers: number;
  articles: number;
  reviews: number;
  photos: number;
  doctorVerifications: number;
  /** Self-registered labs waiting for review (medopd-docs 90). */
  labVerifications?: number;
  /** Listed doctors asking for the full clinic software. */
  clinicSoftware: number;
  /** Clinics asking for the AI add-on (medopd-docs 126). */
  addonRequests?: number;
  /** Doctor and clinic About text waiting for review (medopd-docs 144). */
  aboutText?: number;
}

// Question text may contain health details, so bodies are kept out of the dev-console request log.
const json = (method: string, body?: unknown) => ({
  method, body: body === undefined ? undefined : JSON.stringify(body), skipBodyLog: true,
});
const id = (v: string) => encodeURIComponent(v);

export const healthQaModule = {
  patient: {
    mine: () => fetchPatientApi<MyQuestion[]>('/api/patient/questions'),
    /** One page of the patient's questions, newest first (medopd-docs 117). */
    minePage: (page: number, size: number) =>
      fetchPatientApi<PagedResponse<MyQuestion>>(`/api/patient/questions?page=${page}&size=${size}`),
    ask: (input: AskInput) => fetchPatientApi<MyQuestion>('/api/patient/questions', json('POST', input)),
    withdraw: (questionId: string) => fetchPatientApi<MyQuestion>(`/api/patient/questions/${id(questionId)}/withdraw`, json('POST')),
  },
  doctor: {
    open: (specialty?: string) =>
      fetchApi<OpenQuestion[]>(`/api/doctor/questions${specialty ? `?${new URLSearchParams({ specialty }).toString()}` : ''}`),
    /** One page of open questions, newest first, optionally one topic — reaches every open question (medopd-docs 117). */
    openPage: (page: number, size: number, specialty?: string) => {
      const params = new URLSearchParams({ page: String(page), size: String(size) });
      if (specialty) params.set('specialty', specialty);
      return fetchApi<PagedResponse<OpenQuestion>>(`/api/doctor/questions?${params.toString()}`);
    },
    /** Topics that have open questions (the filter chips). */
    topics: () => fetchApi<string[]>('/api/doctor/questions/topics'),
    answers: () => fetchApi<MyAnswer[]>('/api/doctor/questions/answers'),
    answer: (questionId: string, body: string) => fetchApi<MyAnswer>(`/api/doctor/questions/${id(questionId)}/answer`, json('PUT', { body })),
    withdraw: (answerId: string) => fetchApi<MyAnswer>(`/api/doctor/questions/answers/${id(answerId)}/withdraw`, json('POST')),
  },
  review: {
    queue: () => fetchApi<QaReviewQueue>('/api/admin/questions/review'),
    /** @param overrideReason needed when the automatic check asked for one (medopd-docs 146) */
    approveQuestion: (questionId: string, overrideReason?: string) => fetchApi<{ status: string }>(
      `/api/admin/questions/${id(questionId)}/approve`, json('POST', overrideReason ? { overrideReason } : undefined)),
    rejectQuestion: (questionId: string, note: string) =>
      fetchApi<{ status: string }>(`/api/admin/questions/${id(questionId)}/reject`, json('POST', { note })),
    approveAnswer: (answerId: string, overrideReason?: string) => fetchApi<{ status: string }>(
      `/api/admin/questions/answers/${id(answerId)}/approve`, json('POST', overrideReason ? { overrideReason } : undefined)),
    rejectAnswer: (answerId: string, note: string) =>
      fetchApi<{ status: string }>(`/api/admin/questions/answers/${id(answerId)}/reject`, json('POST', { note })),
    /** Live answers, newest first — to take one down again. */
    published: () => fetchApi<LiveAnswer[]>('/api/admin/questions/published'),
    // Paged review lists with totals (medopd-docs 112): queue()/published() stop at 100 rows.
    pendingQuestionsPage: (page: number, size: number) =>
      fetchApi<PagedResponse<QuestionForReview>>(`/api/admin/questions/review/questions?page=${page}&size=${size}`),
    pendingAnswersPage: (page: number, size: number) =>
      fetchApi<PagedResponse<AnswerForReview>>(`/api/admin/questions/review/answers?page=${page}&size=${size}`),
    publishedPage: (page: number, size: number) =>
      fetchApi<PagedResponse<LiveAnswer>>(`/api/admin/questions/published?page=${page}&size=${size}`),
    retractAnswer: (answerId: string, note: string) =>
      fetchApi<{ status: string }>(`/api/admin/questions/answers/${id(answerId)}/retract`, json('POST', { note })),
    closeQuestion: (questionId: string, note: string) =>
      fetchApi<{ status: string }>(`/api/admin/questions/${id(questionId)}/close`, json('POST', { note })),
    /** How much is waiting in every super-admin review queue (questions, answers, articles, reviews, photos, verifications). */
    pendingCounts: () => fetchApi<ModerationPendingCounts>('/api/admin/moderation/pending-counts'),
  },
};
